import { Injectable } from '@angular/core';

import { SnLonLat, SnLonLatBounds, SnLonLatLike } from '@lib/geo/helpers';
import { IMarker as _IMarker } from '@lib/geo/map';
import { MapGeometryFacadeService, DistanceUnit } from '@lib/map-geometry';

import { GoogleMap } from '../../../map';
import { IGoogleMarker } from '../interfaces/google-marker.interface';


type IMarker = _IMarker<GoogleMap>;

@Injectable()
export class GoogleMarkerGeometryService {
  private _googleMarkers = new Map<string, IGoogleMarker>();

  constructor(private readonly _mapGeometryService: MapGeometryFacadeService<GoogleMap>) {}

  setMarkersCollection(googleMarkers: Map<string, IGoogleMarker>): void {
    this._googleMarkers = googleMarkers;
  }

  setVisible(googleMarker: IGoogleMarker, marker: IMarker, visible: boolean): void {
    if ('setVisible' in googleMarker && typeof googleMarker.setVisible === 'function') {
      googleMarker.setVisible(visible);
    }
    marker.visible = visible;
  }

  setPosition(googleMarker: IGoogleMarker, marker: IMarker, position: SnLonLat): void {
    googleMarker.setDestination(position);
    marker.position = position;
  }

  getMarkerPosition(marker: IMarker): SnLonLat {
    return marker.position;
  }

  getMarkerCenter(marker: IMarker): { center: number[] } {
    return { center: [marker.position.lon, marker.position.lat] };
  }

  isVisible(marker: IMarker): boolean {
    return marker.visible;
  }

  calculateDistance(marker1: IMarker, marker2: IMarker, unit: DistanceUnit = DistanceUnit.MILES): number {
    const point1 = new google.maps.LatLng(marker1.position.lat, marker1.position.lon);
    const point2 = new google.maps.LatLng(marker2.position.lat, marker2.position.lon);

    const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(point1, point2);
    return this._mapGeometryService.convertDistance(distanceMeters, DistanceUnit.METERS, unit);
  }

  calculateBearing(marker1: IMarker, marker2: IMarker): number {
    const point1 = new google.maps.LatLng(marker1.position.lat, marker1.position.lon);
    const point2 = new google.maps.LatLng(marker2.position.lat, marker2.position.lon);

    return google.maps.geometry.spherical.computeHeading(point1, point2);
  }

  isWithinDistance(marker: IMarker, point: SnLonLat, distanceKm: number): boolean {
    const distance = this.calculateDistance(marker, { position: point } as IMarker);
    return distance <= distanceKm;
  }

  getMarkersBounds(markers: IMarker[]): SnLonLatBounds | null {
    if (markers.length === 0) return null;

    const bounds = new SnLonLatBounds(
      [markers[0].position.lon, markers[0].position.lat],
      [markers[0].position.lon, markers[0].position.lat],
    );

    markers.forEach((marker) => {
      bounds.extend(new SnLonLat(marker.position.lon, marker.position.lat));
    });

    return bounds;
  }

  getMarkersCenter(markers: IMarker[]): SnLonLat | null {
    if (markers.length === 0) return null;

    const points = markers.map(
      (marker) => new google.maps.LatLng(marker.position.lat, marker.position.lon),
    );

    const bounds = new google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));

    const center = bounds.getCenter();
    return new SnLonLat(center.lng(), center.lat());
  }


  fitBoundsToMarkers(markers: IMarker[]): void {
    if (markers.length === 0) return;

    const bounds = this._mapGeometryService.calculateBoundsFromMarkers(markers);
    if (bounds) {
      this._mapGeometryService.fitBounds(bounds);
    }
  }

  panToMarkers(markers: IMarker[]): void {
    if (markers.length === 0) return;

    const bounds = this._mapGeometryService.calculateBoundsFromMarkers(markers);
    if (bounds) {
      this._mapGeometryService.fitBounds(bounds);
    }
  }




  setFollowMode(googleMarker: IGoogleMarker, marker: IMarker, followMode: boolean): void {
    googleMarker.setFollowMode(followMode);
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.followMode = followMode;
  }

  getFollowMode(marker: IMarker): boolean {
    return marker.metadata?.followMode || false;
  }

  setDestination(googleMarker: IGoogleMarker, marker: IMarker, dest: SnLonLatLike): void {
    googleMarker.setDestination(dest);
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.destination = SnLonLat.convert(dest);
  }

  setDeltas(googleMarker: IGoogleMarker, marker: IMarker, seconds: number): void {
    googleMarker.setDeltas(seconds);
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.animationSeconds = seconds;
  }

  move(googleMarker: IGoogleMarker, marker: IMarker): void {
    googleMarker.move();
    marker.position = googleMarker.getLonLat();
  }

  animateSetLonLat(
    googleMarker: IGoogleMarker,
    marker: IMarker,
    seconds: number,
    moveto: SnLonLatLike,
  ): void {
    googleMarker.animateSetLonLat(seconds, moveto);
    setTimeout(() => {
      marker.position = googleMarker.getLonLat();
    }, seconds * 1000);
  }

  isWithinMapBounds(marker: IMarker): boolean {
    try {
      const bounds = this._mapGeometryService.getBounds();
      const position = marker.position;

      return (
        position.lat >= bounds.getSouth() &&
        position.lat <= bounds.getNorth() &&
        position.lon >= bounds.getWest() &&
        position.lon <= bounds.getEast()
      );
    } catch (error) {
      return true;
    }
  }

  getAnimationState(marker: IMarker): {
    isAnimating: boolean;
    destination?: SnLonLat;
    seconds?: number;
  } {
    return {
      isAnimating: !!(marker.metadata?.destination && marker.metadata?.animationSeconds),
      destination: marker.metadata?.destination,
      seconds: marker.metadata?.animationSeconds,
    };
  }

  getMarkerGeometry(marker: IMarker): unknown {
    return {
      type: 'Point',
      coordinates: [marker.position.lon, marker.position.lat],
    };
  }
}
